// src/pages/super-admin/Dashboard.tsx
import { Link } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Activity,
  CalendarClock,
  CheckCircle2,
  ClipboardList,
  FlaskConical,
  Hospital,
  Users2,
} from "lucide-react";
import { useGetDashboardStatsQuery } from "@/api/dashboardApi";
import DashboardOrders from "./DashboardOrders";
import { useSelector } from "react-redux";
import type { RootState } from "@/store";

export default function SuperAdminDashboard() {
  const { user } = useSelector((state: RootState) => state.auth);
  const isClientRole = user?.role === "CLIENT" || user?.role === "CLIENT_EMPLOYEE";

  const { data: stats, isLoading: statsLoading } = useGetDashboardStatsQuery({
    clientId: user?.clientId,
    role: user?.role,
    email: user?.emailAddress
  });
  const totalLive = (stats?.activeOrders ?? 0) + (stats?.inProgress ?? 0) + (stats?.completedToday ?? 0);

  const quickStats = [
    {
      title: "Scheduled Orders",
      value: statsLoading ? "..." : String(stats?.activeOrders ?? 0),
      icon: CalendarClock,
      tone: "from-indigo-600 to-blue-500",
      label: "Waiting for visit",
    },
    {
      title: "In Progress",
      value: statsLoading ? "..." : String(stats?.inProgress ?? 0),
      icon: Activity,
      tone: "from-sky-600 to-cyan-500",
      label: "Currently at clinic",
    },
    {
      title: "Completed Today",
      value: statsLoading ? "..." : String(stats?.completedToday ?? 0),
      icon: CheckCircle2,
      tone: "from-emerald-600 to-teal-500",
      label: "Results delivered",
    },
  ] as const;

  return (
    <div className="space-y-7">
      <section className="relative overflow-hidden rounded-2xl border bg-slate-950 p-6 text-white shadow-lg md:p-8">
        <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-cyan-500/30 blur-3xl" />
        <div className="pointer-events-none absolute -left-20 bottom-0 h-48 w-48 rounded-full bg-blue-500/20 blur-3xl" />
        <div className="relative flex flex-wrap items-start justify-between gap-5">
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-200">Operations Center</p>
            <h1 className="text-3xl font-bold tracking-tight md:text-4xl">Dashboard</h1>
            <p className="max-w-xl text-sm text-slate-200 md:text-base">
              Live operational snapshot from your database for orders, appointments, and completions.
            </p>
          </div>
          {!isClientRole && (
            <div className="flex items-center gap-3 rounded-xl border border-white/20 bg-white/10 px-4 py-3 backdrop-blur">
              <ClipboardList className="h-5 w-5 text-cyan-200" />
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-slate-200">Live Total</p>
                <p className="text-2xl font-extrabold">{statsLoading ? "..." : totalLive}</p>
              </div>
            </div>
          )}
        </div>
      </section>

      <div className="grid gap-4 md:grid-cols-3">
        {quickStats.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card
              key={stat.title}
              className={`overflow-hidden border-0 bg-gradient-to-br ${stat.tone} text-white shadow-md transition-all duration-300 hover:shadow-xl`}
            >
              <CardHeader className="pb-1">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold text-white/90">{stat.title}</CardTitle>
                  <div className="rounded-lg border border-white/30 bg-white/15 p-2">
                    <Icon className="h-4 w-4" />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-4xl font-black tracking-tight">{stat.value}</p>
                <p className="mt-1 text-xs font-medium uppercase tracking-wide text-white/85">{stat.label}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="rounded-2xl border bg-white p-4 shadow-sm">
        <DashboardOrders />
      </div>

      {!isClientRole && (
        <div className="grid gap-4 md:grid-cols-3">
          <Card className="border-sky-200/80 bg-gradient-to-br from-sky-50 to-white shadow-sm">
            <CardHeader className="pb-2">
              <div className="mb-1 inline-flex h-9 w-9 items-center justify-center rounded-lg bg-sky-100 text-sky-700">
                <Hospital className="h-4 w-4" />
              </div>
              <CardTitle>Lab Management</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-slate-600">Create and manage lab admins and lab-level access.</p>
              <div className="flex gap-2">
                <Button asChild size="sm">
                  <Link to="/super-admin/labs/new">Create Lab</Link>
                </Button>
                <Button variant="outline" asChild size="sm">
                  <Link to="/super-admin/labs">View Labs</Link>
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="border-indigo-200/80 bg-gradient-to-br from-indigo-50 to-white shadow-sm">
            <CardHeader className="pb-2">
              <div className="mb-1 inline-flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700">
                <FlaskConical className="h-4 w-4" />
              </div>
              <CardTitle>Vendor Management</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-slate-600">Manage test vendors, availability, and configurations.</p>
              <Button variant="outline" asChild size="sm">
                <Link to="/super-admin/vendors">View Vendors</Link>
              </Button>
            </CardContent>
          </Card>

          <Card className="border-emerald-200/80 bg-gradient-to-br from-emerald-50 to-white shadow-sm">
            <CardHeader className="pb-2">
              <div className="mb-1 inline-flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
                <Users2 className="h-4 w-4" />
              </div>
              <CardTitle>Client Management</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-slate-600">Manage client organizations and assignment permissions.</p>
              <Button variant="outline" asChild size="sm">
                <Link to="/super-admin/clients">View Clients</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="flex items-center justify-between rounded-xl border bg-slate-50 px-4 py-3">
        <div>
          <p className="text-sm font-semibold text-slate-700">Data Source</p>
          <p className="text-xs text-slate-500">All KPI cards are live from DB and refresh via API query.</p>
        </div>
        <div className="inline-flex items-center gap-2 rounded-full border border-emerald-300 bg-emerald-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-800">
          <span className="h-2 w-2 rounded-full bg-emerald-600" />
          Live
        </div>
      </div>
    </div>
  );
}
